import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Checkbox,
  Box,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { FiAlignJustify } from "react-icons/fi";
import { Close as CloseIcon } from "@mui/icons-material";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// 🔒 Common check for locked fields
const isLockedField = (field) => field.Notmove === true || !!field.pinned;

// Sortable Item
const SortableItem = ({ field, index, handleToggle }) => {
  const isImmovable = isLockedField(field);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.id,
    disabled: isImmovable, // 🔒 Disable drag for locked fields
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        mb: 1,
        px: 2,
        py: 1.2,
        border: "1px solid var(--gray-200)",
        borderRadius: "12px",
        backgroundColor: isDragging ? "var(--brand-50)" : "var(--gray-0)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: isImmovable ? "not-allowed" : isDragging ? "grabbing" : "grab",
        opacity: isImmovable ? 0.7 : 1,
      }}
    >
      {/* Drag Handle */}
      <Box display="flex" alignItems="center">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            mr: 1,
            cursor: isImmovable ? "not-allowed" : "grab",
            pointerEvents: isImmovable ? "none" : "auto",
          }}
        >
          <FiAlignJustify
            style={{
              fontSize: 18,
              color: isImmovable ? "var(--gray-400)" : "var(--gray-500)",
            }}
          />
        </Box>
        <Typography variant="body2">{field.label}</Typography>
      </Box>

      {/* Checkbox */}
      <Checkbox
        checked={field.checked}
        disabled={isImmovable} // 🔒 Disable toggle
        onChange={(e) => {
          e.stopPropagation();
          if (!isImmovable) handleToggle(index);
        }}
        size="small"
        sx={{
          color: "var(--gray-500)",
          "&.Mui-checked": { color: "var(--brand-600)" },
        }}
      />
    </Box>
  );
};

// Main Component
const CustomizeList = ({ onSave, onClose, columns }) => {
  const [fields, setFields] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor));

  // Initialize fields
  useEffect(() => {
    const initializedFields = (columns || []).map((col) => ({
      ...col,
      checked: col.checked ?? true,
      label: col.label || "",
    }));
    setFields(initializedFields);
  }, [columns]);

  // Handle drag reorder
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);

    if (isLockedField(fields[oldIndex])) return;

    const reordered = arrayMove(fields, oldIndex, newIndex);
    setFields(reordered);
  };

  // Toggle individual field
  const handleToggle = (index) => {
    const updated = [...fields];
    if (!isLockedField(updated[index])) {
      updated[index].checked = !updated[index].checked;
      setFields(updated);
    }
  };

  // Toggle all unlockable fields
  const handleSelectAll = () => {
    // check if all unlockable fields are selected
    const allSelected = fields
      .filter((f) => !isLockedField(f))
      .every((f) => f.checked);

    // toggle all unlockable fields
    const updated = fields.map(
      (f) => (isLockedField(f) ? f : { ...f, checked: !allSelected }) // if all selected, unselect; else select
    );

    setFields(updated);
  };

  // Reset
  // Reset
  const handleReset = () => {
    const resetFields = fields.map((f) => ({
      ...f,
      checked: isLockedField(f) ? true : true, // locked stays true, unlocked forced to true
    }));
    setFields(resetFields);
  };

  // Save
  const handleSave = () => {
    onSave(fields);
  };

  // Compute selectAll dynamically
  const selectAll = fields
    .filter((f) => !isLockedField(f))
    .every((f) => f.checked);
  const selectedCount = fields.filter((f) => f.checked).length;

  return (
    <Paper
      sx={{
        width: 500,
        maxHeight: 600,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <Typography variant="h6" fontWeight={600}>
          Customize List
        </Typography>
        <CloseIcon onClick={onClose} sx={{ cursor: "pointer", fontSize: 22 }} />
      </Box>

      <Divider />

      {/* Select All */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        pb={0}
      >
        <Typography variant="subtitle1">Selected ({selectedCount})</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={handleSelectAll}
              size="small"
              sx={{
                color: "var(--gray-500)",
                "&.Mui-checked": { color: "var(--brand-600)" },
              }}
            />
          }
          label="Select All"
        />
      </Box>

      {/* Draggable List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box
            sx={{
              overflowY: "auto",
              maxHeight: "60vh",
              p: 2,
              pr: 1,
              flexGrow: 1,
            }}
          >
            {fields.map((field, index) => (
              <SortableItem
                key={field.id}
                field={field}
                index={index}
                handleToggle={handleToggle}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      <Divider sx={{ mb: 2 }} />

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" gap={2} mr={3} mb={1}>
        <OutLinedButton
          label="Reset"
          onClick={handleReset}
          sx={{ height: 40, width: 100 }}
        />
        <BlackButton label="Save" onClick={handleSave} width="25%" />
      </Box>
    </Paper>
  );
};

export default CustomizeList;
